import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  Feature,
  License,
  FeatureFilters,
  LicenseFilters,
  PaginatedResponse,
} from '../types/licensing.types';

const FEATURES_KEY = 'licensing-features';
const LICENSES_KEY = 'licensing-licenses';

// ========== FEATURES ==========

export const useFeatures = (filters: FeatureFilters = {}) => {
  return useQuery({
    queryKey: [FEATURES_KEY, filters],
    queryFn: async (): Promise<PaginatedResponse<Feature>> => {
      return apiClient.getFeatures(filters);
    },
    staleTime: 30_000,
  });
};

export const useCreateFeature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Feature>) => {
      return apiClient.createFeature(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEATURES_KEY] });
    },
  });
};

export const useUpdateFeature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Feature> }) => {
      return apiClient.updateFeature(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEATURES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LICENSES_KEY] });
    },
  });
};

export const useDeleteFeature = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteFeature(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEATURES_KEY] });
    },
  });
};

// ========== LICENSES ==========

export const useLicenses = (filters: LicenseFilters = {}) => {
  return useQuery({
    queryKey: [LICENSES_KEY, filters],
    queryFn: async (): Promise<PaginatedResponse<License>> => {
      return apiClient.getLicenses(filters);
    },
    staleTime: 30_000,
  });
};

export const useCreateLicense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<License>) => {
      return apiClient.createLicense(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LICENSES_KEY] });
    },
  });
};

export const useUpdateLicense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<License> }) => {
      return apiClient.updateLicense(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LICENSES_KEY] });
    },
  });
};

export const useDeleteLicense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return apiClient.deleteLicense(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LICENSES_KEY] });
    },
  });
};
